import { type ReactNode, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ScoreOverridePopoverProps {
  computedScore: number | null;
  override: number | null;
  readOnly?: boolean;
  onApply: (value: number) => Promise<void>;
  onRemove: () => Promise<void>;
  children: ReactNode;
}

export function ScoreOverridePopover({
  computedScore,
  override,
  readOnly = false,
  onApply,
  onRemove,
  children,
}: ScoreOverridePopoverProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(override?.toString() ?? "");
  const [busy, setBusy] = useState<"apply" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInput(override?.toString() ?? "");
      setError(null);
    }
  }, [open, override]);

  if (readOnly) {
    return <>{children}</>;
  }

  async function handleApply() {
    setError(null);
    const trimmed = input.trim();
    if (trimmed === "") {
      setError("Enter a score between 0 and 100");
      return;
    }
    const num = Number(trimmed);
    if (!Number.isFinite(num) || num < 0 || num > 100) {
      setError("Score must be between 0 and 100");
      return;
    }
    if (num === override) {
      setOpen(false);
      return;
    }
    setBusy("apply");
    try {
      await onApply(Math.round(num));
      setOpen(false);
    } catch {
      setError("Save failed. Try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove() {
    setBusy("remove");
    setError(null);
    try {
      await onRemove();
      setOpen(false);
    } catch {
      setError("Couldn't remove the override.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-4">
        <div>
          <h3 className="text-sm font-medium text-slate-900">Override score</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Computed score:{" "}
            <span className="font-medium text-slate-700">
              {computedScore ?? "—"}
              <span className="text-slate-400">/100</span>
            </span>
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Manual score
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleApply();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
              placeholder="0–100"
              disabled={busy !== null}
              className="h-9"
            />
            <span className="text-xs text-slate-400">/100</span>
          </div>
          {error && (
            <p className="text-xs text-rose-600">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          {override !== null ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={busy !== null}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              {busy === "remove" && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Remove override
            </Button>
          ) : (
            <span />
          )}
          <Button
            size="sm"
            onClick={handleApply}
            disabled={busy !== null}
          variant="primary"
          >
            {busy === "apply" && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
